/**
 * Business-day arithmetic in Africa/Johannesburg (SAST, UTC+2, no DST).
 *
 * A "business day" is any Mon–Fri that is not on the SA public-holiday
 * list (see src/config/zaPublicHolidays.js). Durations are measured in
 * 24-hour business-hour slices: 3 business days = 72 business hours.
 *
 * Used by the bonus / SLA clock — see CLAUDE.md §21.
 */

import { SA_PUBLIC_HOLIDAYS } from '../config/zaPublicHolidays';

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const SA_OFFSET_MS = 2 * HOUR_MS; // SAST = UTC+2, no DST

const HOLIDAY_SET = new Set(SA_PUBLIC_HOLIDAYS);

/**
 * Resolve a value to a millisecond timestamp, or null on failure.
 */
function toMs(value) {
  if (value == null) return null;
  const t = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
}

/**
 * Return the SAST calendar-date parts ({y, m, d, key}) for a timestamp.
 * `key` is the YYYY-MM-DD string used to compare against the holiday set.
 */
function sastDateParts(ts) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Johannesburg',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(ts));
  const y = parseInt(parts.find((p) => p.type === 'year').value, 10);
  const m = parseInt(parts.find((p) => p.type === 'month').value, 10);
  const d = parseInt(parts.find((p) => p.type === 'day').value, 10);
  const key = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  return { y, m, d, key };
}

/**
 * Mon–Fri AND not a SA public holiday, in SAST.
 */
export function isBusinessDay(date) {
  const t = toMs(date);
  if (t === null) return false;
  const { y, m, d, key } = sastDateParts(t);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 Sun … 6 Sat
  if (dow === 0 || dow === 6) return false;
  return !HOLIDAY_SET.has(key);
}

/**
 * Integrate business-time across the half-open interval [start, end).
 * Returns 0 if the interval is empty or invalid. Paused intervals can
 * subsequently be subtracted by the caller.
 *
 * Algorithm: iterate one SAST calendar day at a time, clipping the
 * interval to each day's [00:00, 24:00) and adding the clipped length
 * only when that day is a business day.
 */
export function businessMillisBetween(start, end) {
  const s = toMs(start);
  const e = toMs(end);
  if (s === null || e === null || e <= s) return 0;

  let { y, m, d, key } = sastDateParts(s);
  let dayStart = Date.UTC(y, m - 1, d) - SA_OFFSET_MS; // SAST midnight, as UTC ms
  let total = 0;

  while (dayStart < e) {
    const dayEnd = dayStart + DAY_MS;
    const clipStart = Math.max(s, dayStart);
    const clipEnd = Math.min(e, dayEnd);
    if (clipEnd > clipStart) {
      const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
      const isBiz = dow !== 0 && dow !== 6 && !HOLIDAY_SET.has(key);
      if (isBiz) total += clipEnd - clipStart;
    }
    // Advance to the next SAST calendar day.
    dayStart = dayEnd;
    const next = new Date(Date.UTC(y, m - 1, d + 1));
    y = next.getUTCFullYear();
    m = next.getUTCMonth() + 1;
    d = next.getUTCDate();
    key = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }
  return total;
}

/**
 * Convenience: business-time as a decimal number of 24-hour business days.
 */
export function businessDaysBetween(start, end) {
  return businessMillisBetween(start, end) / DAY_MS;
}

/**
 * Threshold for "≤ 3 business days" in milliseconds. Exposed so the
 * dashboard layer can use a single source of truth (matches CLAUDE.md §21).
 */
export const BONUS_CLOCK_THRESHOLD_MS = 3 * DAY_MS;

/**
 * Resolve a value to SAST calendar-date parts {y, m, d}.
 * Accepts a 'YYYY-MM-DD' string (as produced by a native date input —
 * taken at face value, no timezone shift) or any Date / ISO value (whose
 * SAST wall-clock date is used). Returns null on failure.
 */
function calendarParts(value) {
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return { y: +m[1], m: +m[2], d: +m[3] };
  }
  const t = toMs(value);
  if (t === null) return null;
  const { y, m, d } = sastDateParts(t);
  return { y, m, d };
}

/**
 * Return the previous business day before the given date, as a Date pinned
 * to 12:00 UTC of that calendar day (noon keeps the calendar date stable
 * when formatted in any realistic timezone, including SAST).
 *
 * Companion to isBusinessDay / businessMillisBetween: it steps backwards one
 * calendar day at a time, skipping weekends and SA public holidays, using the
 * SAME holiday source (HOLIDAY_SET above) — no duplicate holiday list.
 *
 * Used for the preparation-request deadline (= appointment_date minus one
 * business day). Returns null on invalid input.
 */
export function previousBusinessDay(date) {
  const parts = calendarParts(date);
  if (!parts) return null;
  let { y, m, d } = parts;
  // Search a generous window backwards (covers any holiday/weekend run).
  for (let i = 0; i < 31; i++) {
    const prev = new Date(Date.UTC(y, m - 1, d - 1));
    y = prev.getUTCFullYear();
    m = prev.getUTCMonth() + 1;
    d = prev.getUTCDate();
    const key = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dow = prev.getUTCDay(); // 0 Sun … 6 Sat
    if (dow !== 0 && dow !== 6 && !HOLIDAY_SET.has(key)) {
      return new Date(Date.UTC(y, m - 1, d, 12));
    }
  }
  return null;
}

/**
 * Return the business day `n` business days AFTER the given date, as a Date
 * pinned to 12:00 UTC of that calendar day (same convention as
 * previousBusinessDay: noon keeps the calendar date stable when formatted).
 *
 * Companion to previousBusinessDay: it steps forwards one calendar day at a
 * time, skipping weekends and SA public holidays, using the SAME holiday
 * source (HOLIDAY_SET above) — no duplicate holiday list. The start date
 * itself is never counted, whether or not it is a business day, so
 * addBusinessDays(Tue 22 Sep 2026, 2) = Fri 25 Sep 2026 (Thu 24 Sep is
 * Heritage Day).
 *
 * Used for the referral next-action default (= received_on plus two
 * business days, CLAUDE.md referral section). `n` must be a non-negative
 * integer; n = 0 returns the start date unchanged (pinned to noon UTC).
 * Returns null on invalid input.
 */
export function addBusinessDays(date, n) {
  const parts = calendarParts(date);
  if (!parts) return null;
  if (!Number.isInteger(n) || n < 0) return null;
  let { y, m, d } = parts;
  if (n === 0) return new Date(Date.UTC(y, m - 1, d, 12));
  let remaining = n;
  // Generous search window: n business days never span more than ~n*2 + 31
  // calendar days even across a long holiday/weekend run.
  for (let i = 0; i < n * 2 + 31; i++) {
    const next = new Date(Date.UTC(y, m - 1, d + 1));
    y = next.getUTCFullYear();
    m = next.getUTCMonth() + 1;
    d = next.getUTCDate();
    const key = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dow = next.getUTCDay(); // 0 Sun … 6 Sat
    if (dow !== 0 && dow !== 6 && !HOLIDAY_SET.has(key)) {
      remaining -= 1;
      if (remaining === 0) return new Date(Date.UTC(y, m - 1, d, 12));
    }
  }
  return null;
}
